import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import {
  AcademicCapIcon,
  BriefcaseIcon,
  CakeIcon,
  ChipIcon,
  ClipboardListIcon,
  ColorSwatchIcon,
  CubeIcon,
  DeviceMobileIcon,
  DotsHorizontalIcon,
  HeartIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SparklesIcon,
  TruckIcon,
  UploadIcon,
} from "@heroicons/react/solid";
import { Button } from "../../Components/Button";
import FormLabel from "../../Components/FormLabel";
import {
  clearInitAccountStatus,
  clearUpdateBusiness,
  requestGetBusiness,
  requestInitAccount,
  requestListAccount,
  requestUpdateBusiness,
  useAccountingDispatch,
  useAccountingState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { cancelAPIRequest, getErrorMessages } from "../../Utils";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { Alert } from "../../Components/Alert";
import {
  BUSINESS_CATEGORIES,
  BUSINESS_ENTITY_TYPES,
  BUSINESS_TYPES_OPTIONS,
} from "../../Constants/business";

// Per-category icon so the sector picker reads at a glance. Keyed by the
// stored category code ('001'..'011', '999') from BUSINESS_CATEGORIES.
const CATEGORY_ICONS = {
  "001": CakeIcon, // Kuliner
  "002": ShoppingBagIcon, // Pakaian
  "003": HeartIcon, // Kesehatan dan kecantikan
  "004": TruckIcon, // Otomotif
  "005": ShoppingCartIcon, // Sembako
  "006": CubeIcon, // Alat Bahan Bangunan
  "007": ChipIcon, // Alat Elektronik
  "008": ColorSwatchIcon, // Kerajinan Tangan
  "009": AcademicCapIcon, // Pendidikan
  "010": BriefcaseIcon, // Jasa
  "011": DeviceMobileIcon, // Pulsa / PPOB
  "999": DotsHorizontalIcon, // Lainnya
};

const OnboardingCOA = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: businessID } = useParams();

  const [showErrorModal, setshowErrorModal] = useState(false);

  // Business-profile completion for a better default chart: the backend
  // shapes the seeded accounts from type/structure/category on the record.
  const [typeSel, setTypeSel] = useState(null);
  const [structureSel, setStructureSel] = useState(null);
  const [categorySel, setCategorySel] = useState(null);
  // True while waiting for the profile update that precedes the init call.
  const [updatingProfile, setUpdatingProfile] = useState(false);

  const dispatch = useBusinessTransactionDispatch();
  const accountingDispatch = useAccountingDispatch();

  const {
    request,
    initAccountLoading,
    submitError,
    initAccountStatus,
    accounts,
    accountLoading,
  } = useAccountingState();

  const {
    loading,
    business,
    businessError,
    updateBusiness,
    error: updateError,
  } = useBusinessTransactionState();

  const businessLoaded = business?.id === businessID;
  const effectiveType = typeSel?.value || business?.type || "";
  const missingType = businessLoaded && !business.type;
  // Structure only applies to incorporated (BUSINESS) entities.
  const missingStructure =
    businessLoaded && !business.structure && effectiveType === "BUSINESS";
  const missingCategory = businessLoaded && !business.category;
  const needsProfile = missingType || missingStructure || missingCategory;

  useEffect(() => {
    if (businessID) {
      requestGetBusiness(dispatch, { id: businessID });
    }
  }, [dispatch, businessID]);

  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      clearInitAccountStatus(accountingDispatch);
      navigate(`/opening-balance?id=${businessID}`, {
        state: { onboarding: true },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  useEffect(() => {
    if (request) {
      cancelAPIRequest(request);
    }
    if (businessID) {
      requestListAccount(accountingDispatch, {
        businessID,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessID]);

  useEffect(() => {
    if (accounts.length > 0) {
      navigate(`/account?id=${businessID}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  useEffect(() => {
    if (submitError) {
      setshowErrorModal(true);
    }
  }, [submitError]);

  // Profile update done → run the actual init.
  useEffect(() => {
    if (updatingProfile && updateBusiness?.id === businessID) {
      clearUpdateBusiness(dispatch);
      setUpdatingProfile(false);
      requestInitAccount(accountingDispatch, { businessID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBusiness?.id, updatingProfile]);

  // Profile update failed → stop waiting, error shows in the alert below.
  useEffect(() => {
    if (updatingProfile && updateError) {
      setUpdatingProfile(false);
    }
  }, [updateError, updatingProfile]);

  const handleInitAccount = () => {
    // Persist any profile answers first — the backend reads structure/type/
    // category off the Business record when it builds the chart.
    const updates = {};
    if (missingType && typeSel?.value) updates.type = typeSel.value;
    if (missingStructure && structureSel?.value)
      updates.structure = structureSel.value;
    if (missingCategory && categorySel?.value)
      updates.category = categorySel.value;

    if (Object.keys(updates).length > 0) {
      setUpdatingProfile(true);
      requestUpdateBusiness(dispatch, {
        input: { id: businessID, ...updates },
      });
    } else {
      requestInitAccount(accountingDispatch, { businessID });
    }
  };

  const OptionCardSkeleton = () => {
    return (
      <div className="bg-white shadow-sm rounded-xl p-8 flex flex-col gap-6 w-full max-w-sm border border-gray-200 animate-pulse">
        <div className="w-14 h-14 rounded-full bg-gray-200 mx-auto" />
        <div className="flex flex-col gap-3 text-center">
          <div className="w-3/4 h-5 bg-gray-200 rounded mx-auto" />
          <div className="w-full h-3 bg-gray-200 rounded" />
          <div className="w-5/6 h-3 bg-gray-200 rounded mx-auto" />
        </div>
        <div className="h-10 w-full bg-gray-200 rounded-lg" />
      </div>
    );
  };

  const OptionCard = ({
    icon = null,
    title = "",
    description = "",
    btnTitle = "",
    primary = false,
    loading = false,
    action = () => {},
  }) => {
    return loading ? (
      <OptionCardSkeleton />
    ) : (
      <div
        onClick={action}
        className={`group relative bg-white rounded-xl p-8 flex flex-col gap-6 w-full max-w-sm border transition-all duration-200 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg ${
          primary
            ? "border-blue-200 hover:border-blue-400 ring-1 ring-transparent hover:ring-blue-100"
            : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto transition-colors ${
            primary
              ? "bg-blue-50 text-blue-600 group-hover:bg-blue-100"
              : "bg-gray-100 text-gray-600 group-hover:bg-gray-200"
          }`}
        >
          {icon}
        </div>
        <div className="flex flex-col gap-2 text-center">
          <span className="font-semibold text-gray-900">{t(title)}</span>
          <p className="text-sm text-gray-500 leading-relaxed">
            {t(description)}
          </p>
        </div>
        <Button
          onClick={(e) => {
            if (e?.stopPropagation) e.stopPropagation();
            action();
          }}
          type="button"
          title={t(btnTitle)}
          outline={!primary}
          loading={loading}
          disabled={loading}
        />
      </div>
    );
  };

  if (!business || businessError) {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert
            type="error"
            title="Error"
            message={getErrorMessages(businessError) || "Business not found."}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {submitError && (
        <ModalConfirm
          open={showErrorModal}
          setOpen={(open) => {
            setshowErrorModal(open);
          }}
          title="Error"
          description={
            (submitError?.response?.body &&
              JSON.parse(submitError.response.body)?.message) ||
            getErrorMessages(submitError)
          }
          type="ALERT"
          onConfirm={() => {
            clearInitAccountStatus(accountingDispatch);
          }}
        />
      )}
      <div className="bg-white border border-gray-200 shadow-sm sm:overflow-hidden rounded-xl sm:min-h-[30rem]">
        <div className="flex flex-col gap-8 justify-center p-8 sm:p-12">
          {/* Header */}
          <div className="flex flex-col items-center gap-4 text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
              <ClipboardListIcon className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("chooseMethodCOA")}
            </h1>
            <p className="text-gray-500 leading-relaxed">{t("coaDescription")}</p>
          </div>

          {updateError && (
            <div className="max-w-md mx-auto w-full">
              <Alert
                type="error"
                title="Error"
                message={getErrorMessages(updateError)}
              />
            </div>
          )}

          {/* Optional profile completion → shapes the seeded chart */}
          {needsProfile && (
            <div className="flex flex-col gap-4 max-w-md mx-auto w-full rounded-xl border border-gray-200 bg-gray-50 p-6">
              <p className="text-sm text-gray-500 text-center">
                {t("initAccountPrompt.profileHint")}
              </p>
              {missingType && (
                <div>
                  <FormLabel
                    htmlFor="initType"
                    label={t("businessType", "Jenis Usaha")}
                  />
                  <Select
                    name="initType"
                    id="initType"
                    className="mt-1 text-sm"
                    classNamePrefix="select2-selection"
                    options={BUSINESS_TYPES_OPTIONS}
                    value={typeSel}
                    onChange={setTypeSel}
                    isClearable
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isDisabled={
                      initAccountLoading || loading || updatingProfile
                    }
                  />
                </div>
              )}
              {missingStructure && (
                <div>
                  <FormLabel
                    htmlFor="initEntityType"
                    label={t("businessEntityType")}
                  />
                  <Select
                    name="initEntityType"
                    id="initEntityType"
                    className="mt-1 text-sm"
                    classNamePrefix="select2-selection"
                    options={BUSINESS_ENTITY_TYPES}
                    placeholder={t("businessEntityInputPlaceholder")}
                    value={structureSel}
                    onChange={setStructureSel}
                    isClearable
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isDisabled={
                      initAccountLoading || loading || updatingProfile
                    }
                  />
                </div>
              )}
              {missingCategory && (
                <div>
                  <FormLabel
                    htmlFor="initCategory"
                    label={t("businessCategory", "Kategori Usaha")}
                  />
                  <div
                    role="radiogroup"
                    aria-label={t("businessCategory", "Kategori Usaha")}
                    className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2"
                  >
                    {BUSINESS_CATEGORIES.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.value] || DotsHorizontalIcon;
                      const selected = categorySel?.value === cat.value;
                      const disabled =
                        initAccountLoading || loading || updatingProfile;
                      return (
                        <button
                          key={cat.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          disabled={disabled}
                          onClick={() =>
                            setCategorySel(selected ? null : cat)
                          }
                          className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border p-2.5 text-center transition-all ${
                            disabled
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer hover:border-blue-300 hover:bg-blue-50/50"
                          } ${
                            selected
                              ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                              : "border-gray-200 bg-white"
                          }`}
                        >
                          <Icon
                            className={`h-5 w-5 ${
                              selected ? "text-blue-600" : "text-gray-400"
                            }`}
                          />
                          <span
                            className={`text-[11px] leading-tight ${
                              selected
                                ? "font-medium text-blue-700"
                                : "text-gray-600"
                            }`}
                          >
                            {cat.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Method cards */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-stretch">
            <OptionCard
              icon={<SparklesIcon className="w-7 h-7" />}
              title="generateAutomatically"
              description="startWithProvidedCOA"
              btnTitle="createNow"
              primary
              action={handleInitAccount}
              loading={
                initAccountLoading ||
                accountLoading ||
                loading ||
                updatingProfile
              }
            />
            <OptionCard
              icon={<UploadIcon className="w-7 h-7" />}
              title="uploadUsingTemplate"
              description="uploadCOAXLSTemplateForAccountIntegration"
              btnTitle="uploadTemplate"
              action={() =>
                navigate(`/account/import?id=${businessID}`, {
                  state: { onboarding: true },
                })
              }
              loading={
                initAccountLoading ||
                accountLoading ||
                loading ||
                updatingProfile
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingCOA;
